/**
 * The only place the browser talks to the server.
 *
 * Money, quantities and rates arrive as decimal strings and are kept that way —
 * nothing here parses a financial value into a JS number.
 */
import type { ComparativeStatement } from './supply-chain-types.js'
import { createContext, useContext } from 'react'
import type { DocumentDef, DocumentStatus, KickoffReadiness, Violation } from '@project/contracts'

const API_BASE = '/api'

/** The demo tenant. A real deployment resolves this from the host name. */
/** The server requires a UUID here; it matches the API's own demo default. */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code: string = 'error',
    readonly violations: readonly Violation[] = [],
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export interface ErrorEnvelope {
  code?: string
  message?: string
  violations?: Violation[]
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null

const readEnvelope = async (res: Response): Promise<ErrorEnvelope> => {
  try {
    const body: unknown = await res.json()
    if (!isRecord(body)) return {}
    const envelope: ErrorEnvelope = {}
    if (typeof body['code'] === 'string') envelope.code = body['code']
    if (typeof body['message'] === 'string') envelope.message = body['message']
    if (Array.isArray(body['violations'])) envelope.violations = body['violations'] as Violation[]
    return envelope
  } catch {
    return {}
  }
}

const idempotencyKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`

/* ---- shapes the server returns ----------------------------------------- */

export interface ListRow {
  readonly id: string
  readonly [key: string]: unknown
}

export interface ListResult {
  readonly rows: readonly ListRow[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
}

/**
 * The document wire shapes are NOT declared here.
 *
 * They live in @project/contracts and the server compiles against the same
 * definitions. That is deliberate: an earlier version declared them on both
 * sides, they drifted until they shared one key, and every document page went
 * blank while `tsc` stayed green — because JSON over HTTP is untyped and
 * nothing compared the two halves. One declaration, two consumers.
 */
export type {
  LineRow,
  ApprovalStep,
  Advisory,
  DetailEntry,
  AmountLine,
  DocumentDetail,
  SessionView,
} from '@project/contracts'
import type {
  DriveFileView,
  DriveCollectionView,
  MasterDef,
  ReportDef,
  DashboardDef,
  LineRow,
  ApprovalStep,
  Advisory,
  DetailEntry,
  AmountLine,
  DocumentDetail,
  RelatedDocView,
  SessionView,
  SettingDef,
  SettingsView,
  EmailCopyOptions,
  EmailTemplateView,
  OutboxMessage,
  ConnectorDef,
  IntegrationConfigView,
  IntegrationRunResult,
  IntegrationSummary,
} from '@project/contracts'

export type {
  EmailCopyOptions, EmailTemplateView, OutboxMessage,
  ConnectorDef, IntegrationConfigView, IntegrationRunResult, IntegrationSummary,
} from '@project/contracts'

/** One document's standing with the accounting system (docs/11 §Integration). */
export interface PendingDocument {
  readonly id: string
  readonly docType: string
  readonly docTypeLabel: string
  readonly route: string
  readonly documentNo: string | null
  readonly documentDate: string
  readonly total: string
  readonly integrationStatus: string
  readonly integrationRef: string | null
  readonly integrationError: string | null
  readonly integrationAt: string | null
}

/* ---- bulk data (docs/11 §Data Imports, §Export Data, §Branding) ---------- */

export type RowAction = 'CREATE' | 'UPDATE' | 'UNCHANGED' | 'REJECT'

export interface ValidatedRow {
  readonly sheetRow: number
  readonly action: RowAction
  readonly id: string | null
  readonly label: string
  readonly values: Readonly<Record<string, unknown>>
  readonly changes: ReadonlyArray<{ field: string; from: string; to: string }>
  readonly problem: string | null
}

export interface ImportJobView {
  readonly id: string
  readonly resource: string
  readonly resourceLabel: string
  readonly fileName: string | null
  readonly state: 'VALIDATED' | 'IMPORTED' | 'FAILED'
  readonly accepted: number
  readonly rejected: number
  readonly createdCount: number
  readonly updatedCount: number
  readonly rows: readonly ValidatedRow[]
  readonly createdByName: string | null
  readonly createdAt: string
  readonly committedAt: string | null
}

export interface ImportResource {
  readonly key: string
  readonly label: string
  readonly group: string
  readonly blurb: string
}

export interface DataExportView {
  readonly id: string
  readonly state: 'REQUESTED' | 'READY' | 'EXPIRED' | 'FAILED'
  readonly requestedByName: string | null
  readonly requestedAt: string
  readonly readyAt: string | null
  readonly expiresAt: string | null
  readonly byteSize: number | null
  readonly downloadable: boolean
  readonly note: string | null
}

export interface BrandingView {
  readonly companyName: string
  /** A PNG data URL, or null when none has been set. */
  readonly logo: string | null
  readonly website: string | null
  readonly tanNo: string | null
  readonly cinNo: string | null
}

export interface IntegrationAttempt {
  readonly id: string
  readonly outcome: string
  readonly connector: string
  readonly reference: string | null
  readonly error: string | null
  readonly at: string
  readonly byName: string | null
}

/** The related-documents tab reads this; the ledger fields ride along. */
export type RelatedDoc = RelatedDocView

export type { DriveFileView as DriveFile, DriveCollectionView as DriveCollection } from '@project/contracts'

/** One entry on the Team Board: an event from the audit log, or a remark. */
export interface FeedEntry {
  readonly id: string
  readonly kind: 'EVENT' | 'REMARK'
  readonly action: string
  readonly text: string
  readonly body: string | null
  readonly authorName: string
  readonly at: string
  readonly attachment: { readonly id: string; readonly fileName: string } | null
  readonly mine: boolean
}

/* ---- the project schedule (docs/20 D1) ----------------------------------- */

export interface ScheduleTask {
  readonly id: string
  readonly parentId: string | null
  readonly boqLineId: string | null
  readonly seq: number
  readonly wbs: string | null
  readonly name: string
  readonly plannedStart: string | null
  readonly plannedFinish: string | null
  readonly forecastStart: string | null
  readonly forecastFinish: string | null
  readonly actualStart: string | null
  readonly actualFinish: string | null
  readonly percentComplete: string
  readonly isMilestone: boolean
  readonly employeeId: string | null
  readonly employeeName: string | null
  readonly remarks: string | null
  readonly state: 'NOT_STARTED' | 'IN_PROGRESS' | 'DONE'
  /** Days late against the planned finish. Negative means ahead. */
  readonly slipDays: number | null
  readonly overdue: boolean
}

export interface ScheduleSummary {
  readonly projectId: string
  readonly taskCount: number
  readonly done: number
  readonly inProgress: number
  readonly overdue: number
  readonly percentComplete: string
  readonly plannedStart: string | null
  readonly plannedFinish: string | null
  readonly forecastFinish: string | null
  readonly slipDays: number | null
}

export interface ScheduleView {
  readonly summary: ScheduleSummary
  readonly tasks: readonly ScheduleTask[]
}

/** Who is on a project, and in what role (docs/05 §1). */
export interface TeamMember {
  readonly id: string
  readonly employeeId: string
  readonly name: string
  readonly designation: string | null
  readonly email: string | null
  readonly mobile: string | null
  readonly roleId: string | null
  readonly roleName: string | null
  readonly notify: boolean
  readonly addedAt: string
  /** Set when this person came in with a whole team rather than on their own. */
  readonly teamId: string | null
  readonly teamName: string | null
}

/** One name on a team's own roster — the `View members` list (docs/05 §5). */
export interface TeamRosterMember {
  readonly id: string
  readonly employeeId: string
  readonly name: string
  readonly roleId: string | null
  readonly roleName: string | null
  readonly mobile: string | null
  readonly email: string | null
}

/* ---- what a buyer looks at before signing (docs/06 §4) ------------------- */

export interface ItemOverviewRow {
  readonly lineId: string
  readonly seq: number
  readonly item: string
  readonly description: string | null
  readonly billable: string
  readonly boqRef: string | null
  /** `Self`, or the project this line is being bought for. */
  readonly forWhom: string
  readonly projectName: string | null
  readonly quantity: string | null
  readonly unit: string | null
  readonly rate: string | null
  readonly discountPct: string
  readonly amount: string | null
  /** Free stock of this material everywhere, so the approver can check it. */
  readonly stockOnHand: string | null
}

export interface RateComparisonRow {
  readonly lineId: string
  readonly item: string
  readonly description: string | null
  readonly brand: string | null
  readonly quantity: string | null
  readonly unit: string | null
  readonly estimatedRate: string | null
  readonly boqRate: string | null
  readonly orderedRate: string | null
  readonly quotedRate: string | null
  /** Positive means the order is cheaper than the estimate. */
  readonly differenceAmount: string | null
  readonly differencePct: string | null
}

export interface PriceHistoryRow {
  readonly source: 'PURCHASE' | 'RATE_CONTRACT' | 'SUPPLIER_QUOTE' | 'SUBCONTRACT'
  readonly documentId: string
  readonly documentNo: string | null
  readonly route: string
  readonly documentDate: string
  readonly supplierName: string | null
  readonly brand: string | null
  readonly quantity: string | null
  readonly unit: string | null
  readonly rate: string | null
  readonly discountPct: string
  readonly finalRate: string | null
  readonly paymentTerm: string | null
  readonly projectName: string | null
}

export interface PriceHistoryResult {
  readonly item: string
  readonly itemId: string | null
  readonly quantity: string | null
  readonly unit: string | null
  readonly rate: string | null
  readonly brand: string | null
  readonly finalRate: string | null
  readonly brands: readonly string[]
  readonly rows: readonly PriceHistoryRow[]
}

/** A candidate line from the BOQ or from stock. */
export interface PickLine {
  readonly fromLineId?: string
  readonly itemId: string | null
  readonly itemLabel: string
  readonly description: string | null
  readonly unit: string | null
  readonly rate: string | null
  readonly availableQty: string
  readonly openQty: string
  readonly note?: string
  /** The customer's own reference for the BOQ line this sits under. */
  readonly boqCode?: string | null
  /** What `BOQ View` groups by, and the narrative it shows above the group. */
  readonly boqLineId?: string | null
  readonly boqDescription?: string | null
  /** `T` or `NT` in the picker — extra work outside the original tender. */
  readonly tendered?: boolean
  /** Set on the estimate and carried onto the line the picker creates. */
  readonly isBillable?: boolean
  readonly brand?: string | null
  readonly hsnSacCode?: string | null
  /** The billing term the BOQ line is billed under, carried onto the line. */
  readonly billingTermLineId?: string | null
  /** How much of a BOQ line is ready to be billed, and how much already was. */
  readonly calculatedQty?: string
  readonly billedQty?: string
  /** Free stock for this item, everywhere, so it is not bought twice. */
  readonly stockOnHand?: string
  /** Further cells the new row starts with — a need-by date, the job it is for. */
  readonly carry?: Readonly<Record<string, string>>
}

/** One RFQ line as every supplier priced it. */
export interface QuoteComparisonRow {
  readonly lineId: string
  readonly item: string
  readonly description: string | null
  readonly quantity: string | null
  readonly unit: string | null
  readonly estimatedRate: string | null
  readonly quotes: ReadonlyArray<{
    readonly supplierId: string
    readonly supplierName: string
    readonly rate: string | null
    readonly amount: string | null
    readonly lowest: boolean
  }>
}

export interface QuoteComparison {
  readonly suppliers: ReadonlyArray<{ readonly id: string; readonly name: string; readonly total: string }>
  readonly rows: readonly QuoteComparisonRow[]
}

export interface PickResult {
  readonly source: string
  readonly title: string
  readonly emptyReason: string | null
  readonly lines: readonly PickLine[]
}

export interface ImportResult {
  readonly accepted: readonly Record<string, unknown>[]
  readonly rejected: ReadonlyArray<{ sheetRow: number; problem: string | null }>
  readonly ignoredHeaders: readonly string[]
  readonly summary: string
}

/* ---- administration (docs/11) -------------------------------------------- */

export interface UserView {
  readonly id: string
  readonly name: string
  readonly email: string
  readonly role: string
  readonly active: boolean
  readonly lastLoginAt: string | null
  readonly mustChangePassword: boolean
  readonly lockedUntil: string | null
  /** The employee record this sign-in is, in the current company. */
  readonly employee: {
    readonly id: string
    readonly name: string
    readonly designation: string | null
    readonly roleName: string | null
  } | null
}

/** Who someone is in the business, given when they are added. */
export interface NewPersonDetails {
  readonly designation?: string | null
  readonly mobile?: string | null
  readonly roleId?: string | null
  readonly employeeId?: string | null
}

export interface NumberSeriesView {
  readonly id: string
  readonly docType: string
  readonly docTypeLabel: string
  readonly prefix: string
  readonly nextValue: number
  readonly width: number
  readonly financialYear: string
  readonly active: boolean
  readonly format: string
  readonly scope: string
  readonly resetPeriod: string
  readonly example: string
}

export interface WorkflowView {
  readonly id: string
  readonly docType: string
  readonly docTypeLabel: string
  readonly name: string
  readonly version: number
  /** LIVE routes documents, DRAFT is written down but not in effect. */
  readonly status: 'LIVE' | 'DRAFT' | 'ARCHIVED'
  readonly isLive: boolean
  readonly stages: ReadonlyArray<{
    order: number
    name: string
    action: string
    approvers: readonly string[] | 'ANYONE_WITH_PERMISSION'
  }>
  readonly inFlight: number
}

/** One line of the changelog (docs/11 §Permissions `Changelog`, R-10.1). */
export interface ChangelogEntry {
  readonly id: string
  readonly entity: string
  readonly entityLabel: string
  readonly entityId: string
  readonly action: string
  readonly actionLabel: string
  readonly actorName: string
  readonly at: string
  readonly before: Record<string, unknown> | null
  readonly after: Record<string, unknown> | null
}

/** Role × document type → what may be done (docs/15 §10.1). */
export interface PermissionRow {
  /** A sign-in role, or the code of a role the business named for itself. */
  readonly role: string
  readonly roleLabel: string
  readonly docType: string
  readonly docTypeLabel: string
  readonly level: string
  readonly configured: boolean
}

export interface UserAccessView {
  readonly userId: string
  readonly name: string
  readonly userType: string
  readonly delegateExpiresAt: string | null
  readonly projectScope: string
  /** docs/15 R-10.2 — the four scopes that are not about projects. */
  readonly toolsScope: string
  readonly stockPurchaseScope: string
  readonly reportDownloadScope: string
  readonly expenseReportScope: string
  readonly companyIds: readonly string[]
  readonly projectIds: readonly string[]
  readonly companies: ReadonlyArray<{ id: string; name: string }>
  readonly projects: ReadonlyArray<{ id: string; name: string }>
}

export interface LoginEventView {
  readonly id: string
  readonly email: string
  readonly at: string
  readonly ip: string | null
  readonly browser: string | null
  readonly os: string | null
  readonly outcome: string
  readonly userId: string | null
}

export interface CompanyView {
  readonly id: string
  readonly name: string
  readonly legalName: string
  readonly abbreviation: string
  readonly gstin: string | null
  readonly panNo: string | null
  readonly email: string | null
  readonly active: boolean
}

/* ---- print templates (docs/11 §Print Templates) -------------------------- */

export interface PrintTemplateView {
  readonly id: string
  readonly docType: string
  readonly docTypeLabel: string
  readonly name: string
  readonly fileName: string | null
  readonly body: string
  readonly isDefault: boolean
  readonly isStandard: boolean
  readonly appliesToAll: boolean
  readonly active: boolean
  readonly updatedAt: string | null
  readonly updatedByName: string | null
}

/** One entry per document type, whether or not a template has been written. */
export interface TemplateSlot {
  readonly docType: string
  readonly docTypeLabel: string
  readonly group: string
  readonly templates: readonly PrintTemplateView[]
}

export interface TemplateHelper {
  readonly name: string
  readonly usage: string
  readonly means: string
}

/** A named set of filters and columns for one list (docs/02 §3). */
export interface SavedView {
  readonly id: string
  readonly scope: string
  readonly name: string
  readonly ownerName: string
  readonly mine: boolean
  readonly shared: boolean
  readonly isDefault: boolean
  readonly filters: Readonly<Record<string, string>>
  readonly columns: readonly string[]
}

/** A standing question about the documents (docs/11 §Work List). */
export interface WorkListSummary {
  readonly key: string
  readonly label: string
  readonly blurb: string
  readonly clearedBy: string
  readonly groupedUnder: string
  readonly groupLabel: string
  readonly enabled: boolean
  readonly count: number
  readonly action: { readonly key: string; readonly label: string } | null
}

export interface WorkListRow {
  readonly documentId: string
  readonly docType: string
  readonly route: string
  readonly documentNo: string | null
  readonly documentDate: string
  readonly partyName: string | null
  readonly projectName: string | null
  readonly total: string
  readonly waitingDays: number
  readonly detail: string | null
}

export interface DocumentSummary {
  readonly key: string
  readonly route: string
  readonly label: string
  readonly labelPlural: string
  readonly group?: string
  /** The one-line description shown on the menu tile (DocumentDef.blurb). */
  readonly blurb?: string
  readonly description?: string
  readonly favourite?: boolean
  /** Switched off in Settings: still routable, but off the menu. */
  readonly hidden?: boolean
}

export interface InboxCounts { readonly approvals: number; readonly tasks: number; readonly alerts: number }

export interface ApprovalItem {
  readonly id: string; readonly docType: string; readonly docTypeLabel: string; readonly route: string
  readonly documentNo: string | null; readonly documentDate: string
  readonly partyName: string | null; readonly projectName: string | null
  readonly total: string; readonly stageName: string
  readonly waitingDays: number; readonly approvedWithExceptions: boolean
}

export interface NotificationItem {
  readonly id: string; readonly kind: string; readonly title: string; readonly body: string | null
  readonly docType: string | null; readonly route: string | null; readonly documentId: string | null
  readonly actorName: string | null; readonly readAt: string | null; readonly createdAt: string
}

export interface TaskItem {
  readonly id: string; readonly kind: string; readonly title: string; readonly detail: string | null
  readonly docType: string | null; readonly route: string | null; readonly documentId: string | null
  readonly dueDate: string | null; readonly state: string; readonly overdue: boolean
}

/** One document line behind a report row — the `DETAILS` modal (docs/10). */
export interface DrillRow {
  readonly id: string
  readonly docType: string
  readonly docTypeLabel: string
  readonly route: string
  readonly documentNo: string | null
  readonly documentDate: string
  readonly documentStatus: string
  readonly partyName: string | null
  readonly projectName: string | null
  readonly itemName: string | null
  readonly quantity: string | null
  readonly rate: string | null
  readonly amount: string | null
}

export interface DrillResult {
  readonly label: string
  readonly subject: string
  readonly rows: readonly DrillRow[]
  readonly total: string
}

export interface ReportResult {
  readonly rows: ReadonlyArray<Record<string, unknown>>
  readonly totals: Readonly<Record<string, string>>
  readonly count: number
}

/**
 * `id` is the project, supplier or job type a row was grouped by. It travels so
 * the row can be opened — a bar naming a project that cannot show you that
 * project's documents is a number nobody can act on (docs/03 `Show details`).
 */
export type WidgetData =
  | { readonly kind: 'kpi'; readonly items: ReadonlyArray<{ label: string; value: string }> }
  | { readonly kind: 'split'; readonly total: string; readonly parts: ReadonlyArray<{ label: string; value: string; pct: string; id?: string }> }
  | { readonly kind: 'rank'; readonly items: ReadonlyArray<{ label: string; value: string; id?: string }> }
  | { readonly kind: 'series'; readonly labels: readonly string[]; readonly series: ReadonlyArray<{ name: string; points: readonly string[] }> }
  | { readonly kind: 'gauge'; readonly items: ReadonlyArray<{ label: string; value: string; pct: string }> }
  | { readonly kind: 'empty'; readonly note: string }

export interface DashboardResult {
  readonly widgets: Readonly<Record<string, WidgetData>>
}

export interface MasterListResult {
  readonly rows: ReadonlyArray<Record<string, unknown>>
  readonly total: number
  readonly page: number
  readonly pageSize: number
}

/** A parent-document line this document may still allocate against. */
export interface SourceLine {
  readonly fromLineId: string
  readonly seq: number
  readonly itemId: string | null
  readonly itemLabel: string
  readonly description: string | null
  readonly brand: string | null
  readonly unit: string | null
  readonly rate: string | null
  readonly orderedQty: string
  readonly openQty: string
  /** Carried down from the BOQ line, so a challan can split itself. */
  readonly isBillable: boolean
  /** Present when the document names a receipt: what that receipt took in. */
  readonly receivedQty?: string
  /** What the source line itself agreed, rather than what the master says. */
  readonly hsnSacCode: string | null
  readonly taxPct: string | null
  readonly discountPct: string | null
  readonly billingTermLineId: string | null
  /** Further cells the pulled row starts with (docs/24). */
  readonly carry?: Readonly<Record<string, string>>
  /** A better starting quantity than all that is open — what a dispatch advice sent. */
  readonly suggestedQty?: string
}

/**
 * What a new document starts with when it is raised from another, and what
 * each of those ids is called — a lookup shows what it is given, so an id with
 * no label beside it draws an empty box over a filled-in field.
 */
export interface CreateSeed {
  readonly values: Record<string, unknown>
  readonly labels: Record<string, string>
}

/** One answer from the home page's search box. */
export interface SearchHit {
  readonly kind: 'document' | 'master' | 'type'
  readonly id: string
  readonly label: string
  readonly secondary: string | null
  readonly group: string
  readonly route: string
}

export interface LookupOption {
  readonly id: string
  readonly label: string
  readonly secondary?: string | null
}

export interface Company {
  readonly id: string
  readonly name: string
}

export interface HomePayload {
  readonly greetingName: string
  readonly unreadNotifications: number
  readonly company: Company
  readonly favourites: readonly { readonly key: string; readonly label: string; readonly route: string; readonly hub: string }[]
}

export type ActionResult =
  | { readonly ok: true; readonly documentStatus: DocumentStatus; readonly withExceptions: boolean }
  | { readonly ok: false; readonly violations: readonly Violation[] }

/* ---- the client -------------------------------------------------------- */

export interface Api {
  readonly companyId: string
  session(): Promise<SessionView>
  login(email: string, password: string): Promise<SessionView>
  logout(): Promise<void>
  documentMenu(): Promise<readonly DocumentSummary[]>
  documentDef(key: string): Promise<DocumentDef>
  companies(): Promise<readonly Company[]>
  home(): Promise<HomePayload>
  setFavourite(key: string, favourite: boolean): Promise<void>
  list(key: string, params: Readonly<Record<string, string>>): Promise<ListResult>
  document(key: string, id: string): Promise<DocumentDetail>
  related(key: string, id: string): Promise<readonly RelatedDoc[]>
  sourceLines(key: string, id: string): Promise<readonly SourceLine[]>
  masterMenu(): Promise<readonly MasterDef[]>
  masterDef(key: string): Promise<MasterDef>
  masterList(key: string, params: Readonly<Record<string, string>>): Promise<MasterListResult>
  masterRecord(key: string, id: string): Promise<Record<string, unknown>>
  masterCreate(key: string, fields: Record<string, unknown>): Promise<Record<string, unknown>>
  masterUpdate(key: string, id: string, fields: Record<string, unknown>): Promise<Record<string, unknown>>
  masterSetActive(key: string, id: string, active: boolean): Promise<Record<string, unknown>>
  inboxCounts(): Promise<InboxCounts>
  approvals(): Promise<readonly ApprovalItem[]>
  notifications(unreadOnly?: boolean): Promise<readonly NotificationItem[]>
  markNotificationsRead(id?: string): Promise<void>
  tasks(includeDone?: boolean): Promise<readonly TaskItem[]>
  setTaskState(id: string, state: 'OPEN' | 'DONE' | 'DISMISSED'): Promise<void>
  favourites(): Promise<readonly string[]>
  reportMenu(): Promise<readonly ReportDef[]>
  runReport(key: string, params: Readonly<Record<string, string>>): Promise<ReportResult>
  /** The documents behind one row of a report. */
  reportDetails(key: string, value: string, params: Readonly<Record<string, string>>): Promise<DrillResult>
  dashboardMenu(): Promise<readonly DashboardDef[]>
  runDashboard(key: string, params: Readonly<Record<string, string>>): Promise<DashboardResult>
  drive(key: string, id: string): Promise<readonly DriveFileView[]>
  driveUpload(key: string, id: string, file: { fileName: string; contentType: string; base64: string; collection?: string }): Promise<DriveFileView>
  /** Soft-deletes an attachment; the stored bytes stay for anything else pointing at them. */
  driveRemove(key: string, id: string, fileId: string): Promise<void>
  lookup(entity: string, q: string): Promise<readonly LookupOption[]>
  /** Documents, masters and screens matching what was typed on the home page. */
  search(q: string): Promise<readonly SearchHit[]>
  create(key: string, fields: Record<string, unknown>): Promise<DocumentDetail>
  save(key: string, id: string, body: { version: number; fields: Record<string, unknown>; lines: readonly LineRow[] }): Promise<DocumentDetail>
  act(key: string, id: string, action: string, acknowledged?: readonly string[], reason?: string): Promise<ActionResult>
  /** Copies a document and answers with the new draft, which the page opens. */
  clone(key: string, id: string): Promise<DocumentDetail>
  /**
   * Moves the lines that disagree with a challan's header onto a challan of
   * their own, and answers with that new one.
   */
  splitBillable(key: string, id: string): Promise<DocumentDetail>
  /**
   * What a new document should already know when it is raised from another —
   * the supplier, the project, and the back-reference to the source itself.
   */
  createSeed(key: string, from: string, fromType: string): Promise<CreateSeed>
  /**
   * Re-states an approved document in another currency or under another GSTIN
   * (docs/06 §4). Not a revision: the agreement has not changed.
   */
  restate(key: string, id: string, input: {
    currencyId?: string | null
    forexRate?: string | null
    taxUnitId?: string | null
  }): Promise<DocumentDetail>
  /** Candidate lines from a declared source: the BOQ, or what is on hand. */
  pickLines(key: string, id: string, source: string): Promise<PickResult>
  /** Reads an uploaded workbook into lines. Nothing is saved by this. */
  importLines(key: string, id: string, base64: string): Promise<ImportResult>
  /**
   * The spreadsheet to fill in. With a document it comes down filled in with
   * what is already on it, which is what makes Export BOQ and Import BOQ the
   * same file (docs/13).
   */
  importTemplateUrl(key: string, documentId?: string): string
  workLists(): Promise<readonly WorkListSummary[]>
  workListRows(key: string): Promise<readonly WorkListRow[]>
  setWorkListEnabled(key: string, enabled: boolean): Promise<readonly WorkListSummary[]>
  /** An order has gone out to the supplier; only the business knows that. */
  markSent(documentId: string): Promise<void>
  /** A material request has been planned, so procurement may begin (R-4.5). */
  setPurchasePlan(documentId: string): Promise<void>
  savedViews(scope: string): Promise<readonly SavedView[]>
  saveView(view: {
    id?: string
    scope: string
    name: string
    shared: boolean
    isDefault: boolean
    filters: Readonly<Record<string, string>>
    columns: readonly string[]
  }): Promise<readonly SavedView[]>
  deleteView(id: string): Promise<void>
  adminUsers(): Promise<readonly UserView[]>
  adminCreateUser(name: string, email: string, role: string, details?: NewPersonDetails): Promise<{ user: UserView; temporaryPassword: string }>
  adminSetUserEmployee(userId: string, employeeId: string | null): Promise<readonly UserView[]>
  adminSetRole(id: string, role: string): Promise<readonly UserView[]>
  adminSetActive(id: string, active: boolean): Promise<readonly UserView[]>
  adminResetPassword(id: string): Promise<{ temporaryPassword: string }>
  /** Someone changing their own password; not an administrative act. */
  changePassword(currentPassword: string, newPassword: string): Promise<void>
  adminNumberTokens(): Promise<ReadonlyArray<{ token: string; means: string }>>
  adminPermissions(): Promise<readonly PermissionRow[]>
  adminSetPermission(role: string, docType: string, level: string): Promise<readonly PermissionRow[]>
  adminUserAccess(userId: string): Promise<UserAccessView>
  adminSetUserAccess(userId: string, access: {
    userType: string
    projectScope: string
    toolsScope?: string
    stockPurchaseScope?: string
    reportDownloadScope?: string
    expenseReportScope?: string
    companyIds: readonly string[]
    projectIds: readonly string[]
    delegateDays?: number
  }): Promise<UserAccessView>
  /** Ends support access now, and every session it holds (docs/15 R-10.7). */
  adminRevokeDelegate(userId: string): Promise<UserAccessView>
  adminLoginRegister(): Promise<readonly LoginEventView[]>
  adminNumbering(): Promise<readonly NumberSeriesView[]>
  adminSaveNumbering(series: NumberSeriesView): Promise<readonly NumberSeriesView[]>
  adminWorkflows(): Promise<readonly WorkflowView[]>
  adminWorkflowTypes(): Promise<ReadonlyArray<{ key: string; label: string }>>
  adminSaveWorkflow(
    docType: string,
    name: string,
    stages: ReadonlyArray<{ name: string }>,
    asDraft?: boolean,
  ): Promise<readonly WorkflowView[]>
  adminSetWorkflowStatus(id: string, status: 'LIVE' | 'DRAFT' | 'ARCHIVED'): Promise<readonly WorkflowView[]>
  adminChangelog(query?: {
    entity?: string
    entityId?: string
    limit?: number
  }): Promise<readonly ChangelogEntry[]>
  adminCompanies(): Promise<readonly CompanyView[]>
  adminSaveCompany(company: CompanyView): Promise<readonly CompanyView[]>
  /* -- outbound email (docs/11 §Email Templates) -------------------------- */
  /** Queues one approved document to the party it is addressed to. */
  emailDocument(documentId: string): Promise<{ to: string; queued: boolean }>
  mailTemplates(): Promise<readonly EmailTemplateView[]>
  saveMailTemplate(
    key: string,
    subject: string,
    body: string,
    copies?: EmailCopyOptions,
  ): Promise<readonly EmailTemplateView[]>
  resetMailTemplate(key: string): Promise<readonly EmailTemplateView[]>
  outbox(status?: string): Promise<readonly OutboxMessage[]>
  mailStatus(): Promise<{ transportConfigured: boolean }>
  drainOutbox(): Promise<{ sent: number; failed: number; held: number }>

  /* -- print templates ---------------------------------------------------- */
  printTemplates(): Promise<readonly TemplateSlot[]>
  standardPrintTemplate(): Promise<{ body: string }>
  printTemplateHelpers(): Promise<readonly TemplateHelper[]>
  printTemplateFields(docType: string): Promise<readonly string[]>
  savePrintTemplate(input: {
    id?: string
    docType: string
    name: string
    fileName?: string | null
    body: string
    isDefault?: boolean
    appliesToAll?: boolean
    active?: boolean
  }): Promise<readonly PrintTemplateView[]>
  resetPrintTemplate(docType: string): Promise<readonly PrintTemplateView[]>
  /** Renders an unsaved body against a real document and answers with HTML. */
  previewPrintTemplate(docType: string, body: string, documentId?: string): Promise<string>

  /* -- the accounting connection ------------------------------------------ */
  integrationConnectors(): Promise<readonly ConnectorDef[]>
  integrationDocTypes(): Promise<ReadonlyArray<{ key: string; label: string }>>
  integrationConfig(): Promise<IntegrationConfigView>
  saveIntegrationConfig(input: {
    connector: string
    endpointUrl?: string | null
    organisationId?: string | null
    companyBook?: string | null
    accessToken?: string | null
    fromDate?: string | null
    docTypes?: readonly string[]
    autoSend?: boolean
  }): Promise<IntegrationConfigView>
  integrationSummary(): Promise<IntegrationSummary>
  integrationQueue(status?: string): Promise<readonly PendingDocument[]>
  runIntegration(): Promise<IntegrationRunResult>
  requeueDocument(id: string): Promise<readonly PendingDocument[]>
  stopDocument(id: string): Promise<readonly PendingDocument[]>
  markIntegratedByHand(id: string, reference: string): Promise<readonly PendingDocument[]>
  integrationHistory(id: string): Promise<readonly IntegrationAttempt[]>
  reassessIntegration(): Promise<{ queued: number; excluded: number }>

  /* -- bulk master loads and the GST rate update -------------------------- */
  importResources(): Promise<readonly ImportResource[]>
  importHistory(): Promise<ReadonlyArray<Omit<ImportJobView, 'rows'>>>
  importTemplateFor(resource: string): string
  /** Step two: reads the file and says what would happen. Writes nothing. */
  validateImport(resource: string, base64: string, fileName?: string): Promise<ImportJobView>
  /** Step three: replays exactly what step two showed. */
  commitImport(id: string): Promise<ImportJobView>

  /* -- whole-account exports ---------------------------------------------- */
  dataExports(): Promise<readonly DataExportView[]>
  dataExportNextAllowed(): Promise<{ at: string | null }>
  requestDataExport(): Promise<readonly DataExportView[]>
  buildDataExport(id: string): Promise<DataExportView>
  dataExportUrl(id: string): string

  /* -- branding ------------------------------------------------------------ */
  branding(): Promise<BrandingView>
  saveBranding(input: {
    logo?: string | null
    website?: string | null
    tanNo?: string | null
    cinNo?: string | null
  }): Promise<BrandingView>

  /* -- Project Drive, the Team Board and the project team ------------------ */
  driveFiles(query: {
    projectId?: string
    documentId?: string
    collectionId?: string
    fileCategoryId?: string
    includeTeamBoard?: boolean
    q?: string
  }): Promise<readonly DriveFileView[]>
  driveUploadTo(input: {
    fileName: string
    contentType?: string
    base64: string
    projectId?: string
    documentId?: string
    collectionId?: string | null
    fileCategoryId?: string | null
  }): Promise<DriveFileView>
  driveFileUrl(fileId: string): string
  driveDelete(fileId: string): Promise<void>
  fileInto(fileId: string, input: {
    collectionId?: string | null
    fileCategoryId?: string | null
    revision?: string | null
    approvalState?: 'FOR_REVIEW' | 'APPROVED' | 'SUPERSEDED' | null
    itemId?: string | null
  }): Promise<void>
  collections(projectId: string): Promise<readonly DriveCollectionView[]>
  createCollection(input: {
    projectId: string
    name: string
    description?: string | null
    collectionTypeId?: string | null
  }): Promise<readonly DriveCollectionView[]>
  removeCollection(projectId: string, id: string): Promise<readonly DriveCollectionView[]>
  board(query: { documentId?: string; projectId?: string }): Promise<readonly FeedEntry[]>
  postToBoard(input: {
    documentId?: string
    projectId?: string
    body: string
    file?: { fileName: string; contentType?: string; base64: string }
  }): Promise<readonly FeedEntry[]>
  removeRemark(id: string): Promise<void>
  projectTeam(projectId: string): Promise<readonly TeamMember[]>
  addToTeam(projectId: string, input: {
    employeeId: string
    projectRoleId?: string | null
    notify?: boolean
  }): Promise<readonly TeamMember[]>
  removeFromTeam(projectId: string, memberId: string): Promise<readonly TeamMember[]>
  addTeamToProject(projectId: string, teamId: string, notify?: boolean): Promise<readonly TeamMember[]>
  teamRoster(teamId: string): Promise<readonly TeamRosterMember[]>
  addToRoster(teamId: string, input: {
    employeeId: string
    projectRoleId?: string | null
  }): Promise<readonly TeamRosterMember[]>
  removeFromRoster(teamId: string, memberId: string): Promise<readonly TeamRosterMember[]>

  /* -- order insights ------------------------------------------------------ */
  itemsOverview(key: string, id: string): Promise<readonly ItemOverviewRow[]>
  rateComparison(key: string, id: string): Promise<readonly RateComparisonRow[]>
  /** Every quotation against one RFQ, line by line, with the L1 marked. */
  quoteComparison(key: string, id: string): Promise<QuoteComparison>
  priceHistory(key: string, id: string, lineId: string, options?: {
    thisSupplierOnly?: boolean
    brand?: string
  }): Promise<PriceHistoryResult>

  /* -- the supply chain (docs/24) -------------------------------------------- */
  /** Every quotation against an RFQ, landed, ranked and scored. */
  comparativeStatement(rfqId: string): Promise<ComparativeStatement>
  /** Award the RFQ to one quotation; the rest are rejected unless the award is split. */
  awardQuotation(rfqId: string, quotationId: string, rejectOthers?: boolean): Promise<ComparativeStatement>
  /** docs/24 §3.1 — is this job ready to buy for? */
  kickoffReadiness(projectId: string): Promise<KickoffReadiness>
  /** Creates whichever kick-off file categories the company lacks. */
  ensureKickoffCategories(): Promise<void>

  /* -- the project schedule ------------------------------------------------ */
  schedule(projectId: string): Promise<ScheduleView>
  saveScheduleTask(input: {
    id?: string
    projectId: string
    name: string
    wbs?: string | null
    seq?: number
    plannedStart?: string | null
    plannedFinish?: string | null
    forecastStart?: string | null
    forecastFinish?: string | null
    actualStart?: string | null
    actualFinish?: string | null
    percentComplete?: number
    isMilestone?: boolean
    employeeId?: string | null
    remarks?: string | null
  }): Promise<ScheduleView>
  seedSchedule(projectId: string): Promise<ScheduleView>
  removeScheduleTask(projectId: string, id: string): Promise<ScheduleView>

  /** What the switches are, and what each one does. */
  settingDefs(): Promise<readonly SettingDef[]>
  settings(): Promise<SettingsView>
  setSetting(key: string, value: boolean | number | string): Promise<SettingsView>
  /** The printable page's URL; the browser opens and prints it. */
  printUrl(key: string, id: string): string
  /** The running account annexure, from a billing request or a sales invoice. */
  raBillUrl(key: string, id: string): string
  /**
   * The workbook's URL for a list, a master or a report. The browser downloads
   * it directly, so this is a URL rather than a request.
   */
  exportUrl(
    kind: 'docs' | 'masters' | 'reports',
    key: string,
    params: Readonly<Record<string, string>>,
    columns?: readonly string[],
  ): string
}

export const apiFor = (companyId: string): Api => {
  const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
    const headers = new Headers(init.headers)
    headers.set('accept', 'application/json')
    // Identity comes from the session cookie, not from headers. Only the
    // company is still chosen per request, and the server validates it
    // belongs to the signed-in user's tenant before trusting it.
    if (companyId) headers.set('x-company', companyId)
    if (init.body !== undefined) {
      headers.set('content-type', 'application/json')
      // Double-submitting an approval must not create two documents.
      headers.set('idempotency-key', idempotencyKey())
    }

    let res: Response
    try {
      res = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: 'include' })
    } catch {
      throw new ApiError(0, 'The server could not be reached. Check your connection and try again.', 'offline')
    }

    if (!res.ok) {
      const envelope = await readEnvelope(res)
      throw new ApiError(
        res.status,
        envelope.message ?? `The server refused that request (${res.status}).`,
        envelope.code ?? 'error',
        envelope.violations ?? [],
      )
    }
    if (res.status === 204) return undefined as T
    return (await res.json()) as T
  }

  /**
   * The same request, answered as text rather than JSON.
   *
   * A rendered template is HTML, and putting it through `res.json()` on the
   * server only to parse it back here would double-escape every angle bracket.
   */
  const requestText = async (path: string, init: RequestInit = {}): Promise<string> => {
    const headers = new Headers(init.headers)
    headers.set('accept', 'text/html')
    if (companyId) headers.set('x-company', companyId)
    if (init.body !== undefined) {
      headers.set('content-type', 'application/json')
      headers.set('idempotency-key', idempotencyKey())
    }
    let res: Response
    try {
      res = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: 'include' })
    } catch {
      throw new ApiError(0, 'The server could not be reached. Check your connection and try again.', 'offline')
    }
    if (!res.ok) {
      const envelope = await readEnvelope(res)
      throw new ApiError(
        res.status,
        envelope.message ?? `The server refused that request (${res.status}).`,
        envelope.code ?? 'error',
        envelope.violations ?? [],
      )
    }
    return res.text()
  }

  const query = (params: Readonly<Record<string, string>>): string => {
    const usable = Object.entries(params).filter(([, v]) => v !== '')
    if (usable.length === 0) return ''
    return `?${new URLSearchParams(usable).toString()}`
  }

  const runAction = async (key: string, id: string, action: string, acknowledged?: readonly string[], reason?: string): Promise<ActionResult> => {
    try {
      const body = await request<unknown>(`/docs/${key}/${id}/${action}`, {
        method: 'POST',
        // Acknowledging the exceptions in the dialog IS the override. The server
        // takes a single flag and still refuses anything that blocks outright.
        body: JSON.stringify({
          override: (acknowledged?.length ?? 0) > 0,
          // Rejection carries the reason back to whoever raised it.
          ...(reason === undefined ? {} : { reason }),
        }),
      })
      // Only submit answers with a rule verdict. Approve and cancel answer with
      // the updated document, which is itself the success.
      if (isRecord(body) && typeof body['ok'] === 'boolean') return body as ActionResult
      if (isRecord(body) && typeof body['documentStatus'] === 'string') {
        return {
          ok: true,
          documentStatus: body['documentStatus'] as DocumentStatus,
          withExceptions: body['approvedWithExceptions'] === true,
        }
      }
      throw new ApiError(502, 'The server returned an unexpected response.')
    } catch (err) {
      // A rule failure is an outcome of the action, not a transport failure.
      if (err instanceof ApiError && err.violations.length > 0) return { ok: false, violations: err.violations }
      throw err
    }
  }

  return {
    companyId,
    documentMenu: async () => {
      // Tolerate either a bare array or the paged `{ items }` envelope.
      const body = await request<unknown>('/meta/documents')
      if (Array.isArray(body)) return body as readonly DocumentSummary[]
      if (isRecord(body) && Array.isArray(body['items'])) return body['items'] as readonly DocumentSummary[]
      return []
    },
    documentDef: (key) => request<DocumentDef>(`/meta/documents/${key}`),
    session: () => request<SessionView>('/auth/session'),
    login: (email: string, password: string) =>
      request<SessionView>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
    logout: () => request<void>('/auth/logout', { method: 'POST' }),
    sourceLines: (key: string, id: string) =>
      request<readonly SourceLine[]>(`/docs/${encodeURIComponent(key)}/${id}/source-lines`),
    masterMenu: () => request<readonly MasterDef[]>('/meta/masters'),
    masterDef: (key: string) => request<MasterDef>(`/meta/masters/${key}`),
    masterList: (key: string, params) => request<MasterListResult>(`/masters/${key}${query(params)}`),
    masterRecord: (key: string, id: string) => request<Record<string, unknown>>(`/masters/${key}/${id}`),
    masterCreate: (key: string, fields) =>
      request<Record<string, unknown>>(`/masters/${key}`, { method: 'POST', body: JSON.stringify({ fields }) }),
    masterUpdate: (key: string, id: string, fields) =>
      request<Record<string, unknown>>(`/masters/${key}/${id}`, { method: 'PUT', body: JSON.stringify({ fields }) }),
    masterSetActive: (key: string, id: string, active: boolean) =>
      request<Record<string, unknown>>(`/masters/${key}/${id}/active`, { method: 'POST', body: JSON.stringify({ active }) }),
    inboxCounts: () => request<InboxCounts>('/inbox/counts'),
    approvals: () => request<readonly ApprovalItem[]>('/approvals'),
    notifications: (unreadOnly?: boolean) =>
      request<readonly NotificationItem[]>(`/notifications${unreadOnly ? '?unreadOnly=true' : ''}`),
    markNotificationsRead: (id?: string) =>
      request<void>(id ? `/notifications/${id}/read` : '/notifications/read', { method: 'POST', body: JSON.stringify({}) }),
    tasks: (includeDone?: boolean) =>
      request<readonly TaskItem[]>(`/tasks${includeDone ? '?includeDone=true' : ''}`),
    setTaskState: (id: string, state: 'OPEN' | 'DONE' | 'DISMISSED') =>
      request<void>(`/tasks/${id}/state`, { method: 'POST', body: JSON.stringify({ state }) }),
    favourites: () => request<readonly string[]>('/favourites'),
    reportMenu: () => request<readonly ReportDef[]>('/meta/reports'),
    runReport: (key: string, params) => request<ReportResult>(`/reports/${key}${query(params)}`),
    reportDetails: (key: string, value: string, params) =>
      request<DrillResult>(`/reports/${key}/details${query({ ...params, value })}`),
    dashboardMenu: () => request<readonly DashboardDef[]>('/meta/dashboards'),
    runDashboard: (key: string, params) => request<DashboardResult>(`/dashboards/${key}${query(params)}`),
    companies: () => request<readonly Company[]>('/companies'),
    home: () => request<HomePayload>('/home'),
    setFavourite: (key, favourite) =>
      request<void>('/favourites', { method: 'POST', body: JSON.stringify({ menuKey: key, on: favourite }) }),
    list: (key, params) => request<ListResult>(`/docs/${key}${query(params)}`),
    document: (key, id) => request<DocumentDetail>(`/docs/${key}/${id}`),
    related: (key, id) => request<readonly RelatedDoc[]>(`/docs/${key}/${id}/related`),
    drive: (key, id) => request<readonly DriveFileView[]>(`/docs/${key}/${id}/drive`),
    driveRemove: (key, id, fileId) =>
      request<void>(`/docs/${key}/${id}/drive/${fileId}/delete`, { method: 'POST', body: JSON.stringify({}) }),
    driveUpload: (key, id, file) =>
      request<DriveFileView>(`/docs/${key}/${id}/drive`, { method: 'POST', body: JSON.stringify(file) }),
    lookup: (entity, q) => request<readonly LookupOption[]>(`/lookups/${entity}${query({ q })}`),
    search: (q: string) => request<readonly SearchHit[]>(`/search${query({ q })}`),
    create: (key, fields) => request<DocumentDetail>(`/docs/${key}`, { method: 'POST', body: JSON.stringify({ fields }) }),
    save: (key, id, body) => request<DocumentDetail>(`/docs/${key}/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    act: runAction,
    clone: (key: string, id: string) =>
      request<DocumentDetail>(`/docs/${key}/${id}/clone`, { method: 'POST', body: JSON.stringify({}) }),
    splitBillable: (key: string, id: string) =>
      request<DocumentDetail>(`/docs/${key}/${id}/split-billable`, { method: 'POST', body: JSON.stringify({}) }),
    createSeed: (key: string, from: string, fromType: string) =>
      request<CreateSeed>(`/docs/${key}/create-seed${query({ from, fromType })}`),
    restate: (key: string, id: string, input) =>
      request<DocumentDetail>(`/docs/${key}/${id}/restate`, { method: 'POST', body: JSON.stringify(input) }),
    pickLines: (key: string, id: string, source: string) =>
      request<PickResult>(`/docs/${key}/${id}/pick/${source}`),
    importLines: (key: string, id: string, base64: string) =>
      request<ImportResult>(`/docs/${key}/${id}/import`, { method: 'POST', body: JSON.stringify({ base64 }) }),
    importTemplateUrl: (key: string, documentId?: string) =>
      `${API_BASE}/docs/${key}/import-template${query({ documentId: documentId ?? '' })}`,
    workLists: () => request<readonly WorkListSummary[]>('/worklists'),
    workListRows: (key: string) => request<readonly WorkListRow[]>(`/worklists/${key}`),
    setWorkListEnabled: (key: string, enabled: boolean) =>
      request<readonly WorkListSummary[]>(`/worklists/${key}/enabled`, { method: 'POST', body: JSON.stringify({ enabled }) }),
    markSent: (documentId: string) =>
      request<void>(`/worklists/mark-sent/${documentId}`, { method: 'POST', body: '{}' }),
    setPurchasePlan: (documentId: string) =>
      request<void>(`/worklists/set-purchase-plan/${documentId}`, { method: 'POST', body: '{}' }),
    savedViews: (scope: string) => request<readonly SavedView[]>(`/views${query({ scope })}`),
    saveView: (view) => request<readonly SavedView[]>('/views', { method: 'POST', body: JSON.stringify(view) }),
    deleteView: (id: string) => request<void>(`/views/${id}/delete`, { method: 'POST', body: '{}' }),
    adminUsers: () => request<readonly UserView[]>('/admin/users'),
    adminCreateUser: (name: string, email: string, role: string, details?: NewPersonDetails) =>
      request<{ user: UserView; temporaryPassword: string }>('/admin/users', {
        method: 'POST', body: JSON.stringify({ name, email, role, ...(details ?? {}) }),
      }),
    adminSetUserEmployee: (userId: string, employeeId: string | null) =>
      request<readonly UserView[]>(`/admin/users/${userId}/employee`, {
        method: 'POST', body: JSON.stringify({ employeeId }),
      }),
    adminSetRole: (id: string, role: string) =>
      request<readonly UserView[]>(`/admin/users/${id}/role`, { method: 'POST', body: JSON.stringify({ role }) }),
    adminSetActive: (id: string, active: boolean) =>
      request<readonly UserView[]>(`/admin/users/${id}/active`, { method: 'POST', body: JSON.stringify({ active }) }),
    adminResetPassword: (id: string) =>
      request<{ temporaryPassword: string }>(`/admin/users/${id}/reset-password`, { method: 'POST', body: '{}' }),
    changePassword: (currentPassword: string, newPassword: string) =>
      request<void>('/admin/password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),
    adminNumberTokens: () => request<ReadonlyArray<{ token: string; means: string }>>('/admin/numbering/tokens'),
    adminPermissions: () => request<readonly PermissionRow[]>('/admin/permissions'),
    adminSetPermission: (role: string, docType: string, level: string) =>
      request<readonly PermissionRow[]>('/admin/permissions', {
        method: 'POST', body: JSON.stringify({ role, docType, level }),
      }),
    adminUserAccess: (userId: string) => request<UserAccessView>(`/admin/users/${userId}/access`),
    adminSetUserAccess: (userId: string, access) =>
      request<UserAccessView>(`/admin/users/${userId}/access`, { method: 'POST', body: JSON.stringify(access) }),
    adminRevokeDelegate: (userId: string) =>
      request<UserAccessView>(`/admin/users/${userId}/revoke-delegate`, { method: 'POST', body: '{}' }),
    adminLoginRegister: () => request<readonly LoginEventView[]>('/admin/login-register'),
    adminNumbering: () => request<readonly NumberSeriesView[]>('/admin/numbering'),
    adminSaveNumbering: (series: NumberSeriesView) =>
      request<readonly NumberSeriesView[]>('/admin/numbering', { method: 'POST', body: JSON.stringify(series) }),
    adminWorkflows: () => request<readonly WorkflowView[]>('/admin/workflows'),
    adminWorkflowTypes: () => request<ReadonlyArray<{ key: string; label: string }>>('/admin/workflows/types'),
    adminSaveWorkflow: (docType: string, name: string, stages: ReadonlyArray<{ name: string }>, asDraft?: boolean) =>
      request<readonly WorkflowView[]>('/admin/workflows', {
        method: 'POST', body: JSON.stringify({ docType, name, stages, asDraft }),
      }),
    adminSetWorkflowStatus: (id: string, status: 'LIVE' | 'DRAFT' | 'ARCHIVED') =>
      request<readonly WorkflowView[]>(`/admin/workflows/${id}/status`, {
        method: 'POST', body: JSON.stringify({ status }),
      }),
    adminChangelog: (q) =>
      request<readonly ChangelogEntry[]>(`/admin/changelog${query({
        entity: q?.entity ?? '',
        entityId: q?.entityId ?? '',
        limit: q?.limit === undefined ? '' : String(q.limit),
      })}`),
    adminCompanies: () => request<readonly CompanyView[]>('/admin/companies'),
    adminSaveCompany: (company: CompanyView) =>
      request<readonly CompanyView[]>('/admin/companies', {
        method: 'POST',
        body: JSON.stringify({ ...company, id: company.id === '' ? undefined : company.id }),
      }),
    emailDocument: (documentId: string) =>
      request<{ to: string; queued: boolean }>(`/mail/send/${documentId}`, { method: 'POST', body: '{}' }),
    mailTemplates: () => request<readonly EmailTemplateView[]>('/mail/templates'),
    saveMailTemplate: (key: string, subject: string, body: string, copies) =>
      request<readonly EmailTemplateView[]>(`/mail/templates/${key}`, {
        method: 'POST', body: JSON.stringify({ subject, body, copies }),
      }),
    resetMailTemplate: (key: string) =>
      request<readonly EmailTemplateView[]>(`/mail/templates/${key}/reset`, { method: 'POST', body: '{}' }),
    outbox: (status?: string) => request<readonly OutboxMessage[]>(`/mail/outbox${query({ status: status ?? '' })}`),
    mailStatus: () => request<{ transportConfigured: boolean }>('/mail/status'),
    drainOutbox: () =>
      request<{ sent: number; failed: number; held: number }>('/mail/drain', { method: 'POST', body: '{}' }),

    printTemplates: () => request<readonly TemplateSlot[]>('/templates'),
    standardPrintTemplate: () => request<{ body: string }>('/templates/standard'),
    printTemplateHelpers: () => request<readonly TemplateHelper[]>('/templates/helpers'),
    printTemplateFields: (docType: string) => request<readonly string[]>(`/templates/fields/${docType}`),
    savePrintTemplate: (input) =>
      request<readonly PrintTemplateView[]>('/templates', { method: 'POST', body: JSON.stringify(input) }),
    resetPrintTemplate: (docType: string) =>
      request<readonly PrintTemplateView[]>(`/templates/reset/${docType}`, { method: 'POST', body: '{}' }),
    previewPrintTemplate: (docType: string, body: string, documentId?: string) =>
      requestText('/templates/preview', {
        method: 'POST',
        body: JSON.stringify({ docType, body, ...(documentId ? { documentId } : {}) }),
      }),

    integrationConnectors: () => request<readonly ConnectorDef[]>('/integration/connectors'),
    integrationDocTypes: () => request<ReadonlyArray<{ key: string; label: string }>>('/integration/doc-types'),
    integrationConfig: () => request<IntegrationConfigView>('/integration/config'),
    saveIntegrationConfig: (input) =>
      request<IntegrationConfigView>('/integration/config', { method: 'POST', body: JSON.stringify(input) }),
    integrationSummary: () => request<IntegrationSummary>('/integration/summary'),
    integrationQueue: (status?: string) =>
      request<readonly PendingDocument[]>(`/integration/queue${query({ status: status ?? '' })}`),
    runIntegration: () => request<IntegrationRunResult>('/integration/run', { method: 'POST', body: '{}' }),
    requeueDocument: (id: string) =>
      request<readonly PendingDocument[]>(`/integration/documents/${id}/requeue`, { method: 'POST', body: '{}' }),
    stopDocument: (id: string) =>
      request<readonly PendingDocument[]>(`/integration/documents/${id}/stop`, { method: 'POST', body: '{}' }),
    markIntegratedByHand: (id: string, reference: string) =>
      request<readonly PendingDocument[]>(`/integration/documents/${id}/manual`, {
        method: 'POST', body: JSON.stringify({ reference }),
      }),
    integrationHistory: (id: string) =>
      request<readonly IntegrationAttempt[]>(`/integration/documents/${id}/history`),
    reassessIntegration: () =>
      request<{ queued: number; excluded: number }>('/integration/reassess', { method: 'POST', body: '{}' }),

    importResources: () => request<readonly ImportResource[]>('/imports/resources'),
    importHistory: () => request<ReadonlyArray<Omit<ImportJobView, 'rows'>>>('/imports/history'),
    importTemplateFor: (resource: string) => `${API_BASE}/imports/template/${encodeURIComponent(resource)}`,
    validateImport: (resource: string, base64: string, fileName?: string) =>
      request<ImportJobView>('/imports/validate', {
        method: 'POST',
        body: JSON.stringify({ resource, base64, ...(fileName ? { fileName } : {}) }),
      }),
    commitImport: (id: string) =>
      request<ImportJobView>(`/imports/${id}/commit`, { method: 'POST', body: '{}' }),

    dataExports: () => request<readonly DataExportView[]>('/data-exports'),
    dataExportNextAllowed: () => request<{ at: string | null }>('/data-exports/next-allowed'),
    requestDataExport: () => request<readonly DataExportView[]>('/data-exports', { method: 'POST', body: '{}' }),
    buildDataExport: (id: string) =>
      request<DataExportView>(`/data-exports/${id}/build`, { method: 'POST', body: '{}' }),
    dataExportUrl: (id: string) => `${API_BASE}/data-exports/${id}/download`,

    branding: () => request<BrandingView>('/admin/branding'),
    saveBranding: (input) =>
      request<BrandingView>('/admin/branding', { method: 'POST', body: JSON.stringify(input) }),

    driveFiles: (q) => request<readonly DriveFileView[]>(`/drive/files${query({
      projectId: q.projectId ?? '',
      documentId: q.documentId ?? '',
      collectionId: q.collectionId ?? '',
      fileCategoryId: q.fileCategoryId ?? '',
      includeTeamBoard: q.includeTeamBoard ? 'true' : '',
      q: q.q ?? '',
    })}`),
    driveUploadTo: (input) =>
      request<DriveFileView>('/drive/files', { method: 'POST', body: JSON.stringify(input) }),
    driveFileUrl: (fileId: string) => `${API_BASE}/drive/files/${fileId}`,
    driveDelete: (fileId: string) =>
      request<void>(`/drive/files/${fileId}/delete`, { method: 'POST', body: '{}' }),
    fileInto: (fileId: string, input) =>
      request<void>(`/drive/files/${fileId}/file-into`, { method: 'POST', body: JSON.stringify(input) }),
    collections: (projectId: string) =>
      request<readonly DriveCollectionView[]>(`/drive/collections${query({ projectId })}`),
    createCollection: (input) =>
      request<readonly DriveCollectionView[]>('/drive/collections', { method: 'POST', body: JSON.stringify(input) }),
    removeCollection: (projectId: string, id: string) =>
      request<readonly DriveCollectionView[]>(`/drive/collections/${id}/delete${query({ projectId })}`, {
        method: 'POST', body: '{}',
      }),
    board: (q) => request<readonly FeedEntry[]>(`/drive/board${query({
      documentId: q.documentId ?? '', projectId: q.projectId ?? '',
    })}`),
    postToBoard: (input) =>
      request<readonly FeedEntry[]>('/drive/board', { method: 'POST', body: JSON.stringify(input) }),
    removeRemark: (id: string) =>
      request<void>(`/drive/board/${id}/delete`, { method: 'POST', body: '{}' }),
    projectTeam: (projectId: string) => request<readonly TeamMember[]>(`/drive/team${query({ projectId })}`),
    addToTeam: (projectId: string, input) =>
      request<readonly TeamMember[]>(`/drive/team/${projectId}`, { method: 'POST', body: JSON.stringify(input) }),
    removeFromTeam: (projectId: string, memberId: string) =>
      request<readonly TeamMember[]>(`/drive/team/${projectId}/${memberId}/delete`, { method: 'POST', body: '{}' }),
    addTeamToProject: (projectId: string, teamId: string, notify?: boolean) =>
      request<readonly TeamMember[]>(`/drive/team/${projectId}/from-team`, {
        method: 'POST', body: JSON.stringify({ teamId, notify }),
      }),
    teamRoster: (teamId: string) => request<readonly TeamRosterMember[]>(`/drive/teams/${teamId}/members`),
    addToRoster: (teamId: string, input) =>
      request<readonly TeamRosterMember[]>(`/drive/teams/${teamId}/members`, {
        method: 'POST', body: JSON.stringify(input),
      }),
    removeFromRoster: (teamId: string, memberId: string) =>
      request<readonly TeamRosterMember[]>(`/drive/teams/${teamId}/members/${memberId}/delete`, {
        method: 'POST', body: '{}',
      }),

    itemsOverview: (key: string, id: string) =>
      request<readonly ItemOverviewRow[]>(`/docs/${key}/${id}/insights/items`),
    rateComparison: (key: string, id: string) =>
      request<readonly RateComparisonRow[]>(`/docs/${key}/${id}/insights/rates`),
    quoteComparison: (key: string, id: string) =>
      request<QuoteComparison>(`/docs/${key}/${id}/insights/quotes`),
    priceHistory: (key: string, id: string, lineId: string, options) =>
      request<PriceHistoryResult>(`/docs/${key}/${id}/insights/price-history/${lineId}${query({
        thisSupplierOnly: options?.thisSupplierOnly ? 'true' : '',
        brand: options?.brand ?? '',
      })}`),

    comparativeStatement: (rfqId: string) =>
      request<ComparativeStatement>(`/supply-chain/rfq/${rfqId}/statement`),
    awardQuotation: (rfqId: string, quotationId: string, rejectOthers?: boolean) =>
      request<ComparativeStatement>(`/supply-chain/rfq/${rfqId}/award`, {
        method: 'POST',
        body: JSON.stringify({ quotationId, ...(rejectOthers === undefined ? {} : { rejectOthers }) }),
      }),
    kickoffReadiness: (projectId: string) =>
      request<KickoffReadiness>(`/supply-chain/kickoff/${projectId}`),
    ensureKickoffCategories: () =>
      request<void>('/supply-chain/kickoff/categories', { method: 'POST', body: '{}' }),
    schedule: (projectId: string) => request<ScheduleView>(`/schedule${query({ projectId })}`),
    saveScheduleTask: (input) =>
      request<ScheduleView>('/schedule', { method: 'POST', body: JSON.stringify(input) }),
    seedSchedule: (projectId: string) =>
      request<ScheduleView>(`/schedule/seed/${projectId}`, { method: 'POST', body: '{}' }),
    removeScheduleTask: (projectId: string, id: string) =>
      request<ScheduleView>(`/schedule/${id}/delete${query({ projectId })}`, { method: 'POST', body: '{}' }),

    settingDefs: () => request<readonly SettingDef[]>('/settings/meta'),
    settings: () => request<SettingsView>('/settings'),
    setSetting: (key: string, value: boolean | number | string) =>
      request<SettingsView>(`/settings/${key}`, { method: 'POST', body: JSON.stringify({ value }) }),
    printUrl: (key: string, id: string) => `${API_BASE}/docs/${key}/${id}/print`,
    raBillUrl: (key: string, id: string) => `${API_BASE}/docs/${key}/${id}/ra-bill`,
    exportUrl: (kind, key, params, columns) =>
      `${API_BASE}/${kind}/${key}/export${query({
        ...params,
        // Page and size are the screen's business, never the export's.
        page: '',
        pageSize: '',
        ...(columns && columns.length > 0 ? { columns: columns.join(',') } : {}),
      })}`,
  }
}

const ApiContext = createContext<Api | null>(null)
export const ApiProvider = ApiContext.Provider

export const useApi = (): Api => {
  const api = useContext(ApiContext)
  if (!api) throw new Error('useApi was called outside the application shell.')
  return api
}
